import {
  EnhancedInfoType,
  GuestQuestionType,
} from "@shared/modules/basket/types/common/serializer-types";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { usePublicBasketField } from "@shared/modules/basket/services/useBasketField";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { CustomerInfoType } from "@shared/modules/customers/components/CustomerInfoForm/CustomerInfoForm.props";
import { UseFormReturn } from "react-hook-form";
import { PropsWithChildren } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import Text from "@ui-kit/typography/Text/Text.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import QuestionForms from "@shared/modules/checkout/components/QuestionForms/QuestionForms.web";

// These are based off the box-office checkout question form components
export default function PublicQuestionForms() {
  throw new Error("This component should not be used directly");
}

// A container for all of the checkout forms
// Used for address fields, enhanced info, custom questions, etc.
PublicQuestionForms.QuestionsContainer = function CheckoutQuestionsContainer({
  multiGuestForm,
  multiGuestItemGroup,
  multiGuestIndex,
  guestQuestionsMap,
  initalValuesForCustomerForm,
  children,
}: PropsWithChildren<{
  multiGuestForm?: UseFormReturn;
  multiGuestItemGroup?: TicketItemGroupType;
  multiGuestIndex?: number;
  guestQuestionsMap: React.MutableRefObject<{
    [key: number]: {
      customQuestions: Array<GuestQuestionType>;
      enhancedInfo: EnhancedInfoType;
    };
  }>;
  initalValuesForCustomerForm?: CustomerInfoType;
}>) {
  const { t } = useTranslation("common");

  const { updateGuestQuestions } = usePublicBasket();

  // All of the customer info fields are required if the item is a membership level
  // EXCEPT for phone and email
  const isMembershipLevel = Boolean(multiGuestItemGroup?.membership_level);

  const requireFirstName =
    multiGuestItemGroup?.collect_info_fields?.collect_info_first_name ||
    usePublicBasketField("collect_info_first_name") ||
    isMembershipLevel;
  const requireLastName =
    multiGuestItemGroup?.collect_info_fields?.collect_info_last_name ||
    usePublicBasketField("collect_info_last_name") ||
    isMembershipLevel;
  const requireName = requireFirstName || requireLastName;
  const requirePhone =
    multiGuestItemGroup?.collect_info_fields?.collect_info_phone_number ||
    usePublicBasketField("collect_info_phone_number");
  const requireEmail =
    multiGuestItemGroup?.collect_info_fields?.collect_info_phone_number ||
    usePublicBasketField("collect_info_email");

  // phone and email are optional fields for memberships
  const hidePhone = !requirePhone && !isMembershipLevel;
  const hideEmail = !requireEmail && !isMembershipLevel;

  return (
    <Box pt="lg">
      <Box
        overflow="hidden"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.400"
      >
        <Flex justifyContent="center" backgroundColor="blue.200">
          <Text p="xs" fontWeight="bold" color="blue.900">
            {multiGuestIndex
              ? t("Guest").concat(` ${multiGuestIndex}`)
              : t("Event Questions")}
          </Text>
        </Flex>
        <Flex gap="xl" p="xl" direction="column">
          {/* Item group information (displayed for info_required tickets ONLY) */}
          {multiGuestItemGroup && (
            <QuestionForms.ItemGroupDetails
              multiGuestItemGroup={multiGuestItemGroup}
            />
          )}
          {/* Customer info form (displayed for info_required tickets ONLY) */}
          {multiGuestForm && (
            <QuestionForms.InfoRequiredCustomerForm
              initialCustomerInfo={initalValuesForCustomerForm}
              multiGuestForm={multiGuestForm}
              multiGuestItemGroup={multiGuestItemGroup}
              guestQuestionsMap={guestQuestionsMap}
              requireName={requireName}
              requirePhone={requirePhone}
              requireEmail={requireEmail}
              onUpdateGuestQuestions={updateGuestQuestions}
              hideFirstName={!requireFirstName}
              hideLastName={!requireLastName}
              hideEmail={hideEmail}
              hidePhone={hidePhone}
            />
          )}
          {/* Other form components */}
          {children}
        </Flex>
      </Box>
    </Box>
  );
};
